import { AnyAction, Reducer } from "redux";
import { Message, Participant } from "@twilio/conversations";

import { ChatState } from "./definitions";
import {
    ACTION_ATTACH_FILES,
    ACTION_ADD_MESSAGE,
    ACTION_ADD_MULTIPLE_MESSAGES,
    ACTION_ADD_PARTICIPANT,
    ACTION_DETACH_FILES,
    ACTION_REMOVE_MESSAGE,
    ACTION_REMOVE_PARTICIPANT,
    ACTION_START_SESSION,
    ACTION_UPDATE_CONVERSATION_STATE,
    ACTION_UPDATE_MESSAGE,
    ACTION_UPDATE_PARTICIPANT,
    ACTION_UPDATE_AWAITING_BOT,
    ACTION_UPDATE_HUMAN_MODE,
    ACTION_UPDATE_AGENT_TYPING,
    ACTION_UPDATE_WELCOME_INPUT_LOCK,
    ACTION_UPDATE_CSAT,
    ACTION_UPDATE_CSAT_PENDING,
    ACTION_RESET_CONVERSATION
} from "./actions/actionTypes";

const initialState: ChatState = {};

function detachFiles(attachedFiles: File[] = [], filesToDetach: File[] = []): File[] {
    return (attachedFiles || []).filter(
        (file: File) =>
            !filesToDetach.some(
                (fileToDetach: File) =>
                    file.name === fileToDetach.name &&
                    file.type === fileToDetach.type &&
                    file.size === fileToDetach.size
            )
    );
}

export const ChatReducer: Reducer = (state: ChatState = initialState, action: AnyAction): ChatState => {
    switch (action.type) {
        case ACTION_START_SESSION: {
            return {
                ...state,
                conversationsClient: action.payload.conversationsClient,
                conversation: action.payload.conversation,
                conversationState: action.payload.conversationState,
                users: action.payload.users,
                participants: action.payload.participants,
                messages: action.payload.messages,
                // CSAT é POR-CONVERSA (chave = conversationSid). Uma nova sessão (nova conversa OU resume)
                // precisa zerar o survey da conversa anterior — senão o useCsat vê `survey` truthy e pula
                // a busca do CSAT desta conversa (o cliente avaliou a A e a B não mostraria a pesquisa).
                csat: undefined,
                // O SINAL segue a mesma regra do survey acima, e pelo mesmo motivo: ele foi carimbado
                // nos attributes da conversa ANTERIOR. Mantê-lo faria o card montar na conversa nova e
                // disparar o GET de uma pesquisa que não é dela.
                csatPending: undefined
            };
        }
        case ACTION_ADD_MULTIPLE_MESSAGES: {
            return {
                ...state,
                messages: [...action.payload.messages, ...(state.messages || [])]
            };
        }
        case ACTION_ADD_MESSAGE: {
            return {
                ...state,
                messages: [...(state.messages || []), action.payload.message]
            };
        }
        case ACTION_REMOVE_MESSAGE: {
            return {
                ...state,
                messages: [...(state.messages || []).filter((m) => m.sid !== action.payload.message.sid)]
            };
        }
        case ACTION_UPDATE_MESSAGE: {
            return {
                ...state,
                messages: [
                    ...(state.messages || []).reduce((acc: Message[], m) => {
                        if (m.sid === action.payload.message.sid) {
                            acc.push(action.payload.message);
                        } else {
                            acc.push(m);
                        }
                        return acc;
                    }, [])
                ]
            };
        }
        case ACTION_ATTACH_FILES: {
            return {
                ...state,
                attachedFiles: [...(state.attachedFiles || []), ...action.payload.filesToAttach]
            };
        }
        case ACTION_DETACH_FILES: {
            const filesToDetach = action.payload.filesToDetach as File[];
            return {
                ...state,
                attachedFiles: detachFiles(state.attachedFiles, filesToDetach)
            };
        }
        case ACTION_ADD_PARTICIPANT: {
            return {
                ...state,
                participants: [...(state.participants || []), action.payload.participant],
                users: [...(state.users || []), action.payload.user]
            };
        }
        case ACTION_REMOVE_PARTICIPANT: {
            return {
                ...state,
                participants: [...(state.participants || []).filter((p) => p.sid !== action.payload.participant.sid)],
                users: [...(state.users || []).filter((u) => u.identity !== action.payload.participant.identity)]
            };
        }
        case ACTION_UPDATE_PARTICIPANT: {
            return {
                ...state,
                participants: [
                    ...(state.participants || []).reduce((acc: Participant[], p) => {
                        if (p.sid === action.payload.participant.sid) {
                            acc.push(action.payload.participant);
                        } else {
                            acc.push(p);
                        }
                        return acc;
                    }, [])
                ]
            };
        }
        case ACTION_RESET_CONVERSATION: {
            /*
             * Só a conversa e o estado dela saem. Mensagens/participantes seguem visíveis até o
             * `ACTION_START_SESSION` da conversa nova substituir tudo — apagar aqui tiraria o
             * histórico da tela do cliente antes da hora.
             */
            return {
                ...state,
                conversation: undefined,
                conversationState: undefined,
                /*
                 * `humanMode` TEM que sair junto. Achado no N3 contra o Twilio real (2026-08-28):
                 * ele sobrevivia à troca de conversa, então a conversa seguinte — só com a IA —
                 * nascia "humana". Como o selector `isConversationEnded` só trata `inactive` como
                 * encerrada FORA do modo humano, o timer de inatividade
                 * virava o estado e a despedida NUNCA aparecia: o cliente ficava preso olhando um
                 * composer que não entrega mais nada.
                 *
                 * Limpo aqui e não no `ACTION_START_SESSION` porque o START também roda no *resume*
                 * de uma conversa humana em andamento, onde o modo humano precisa ser preservado.
                 */
                humanMode: undefined,
                /*
                 * Vazamento de estado e PII entre atendimentos (achados #5 e #6 do code review da pilha, 2026-09-02):
                 *
                 * 1. `attachedFiles`: se a conversa A termina com arquivo anexado não enviado no composer,
                 *    o anexo reaparecia no composer da conversa B ao clicar em "Iniciar nova conversa".
                 *    Como o anexo típico no widget é print de documento pessoal com CPF/dados do apostador,
                 *    o arquivo podia ser enviado por engano para outro atendente humano — vazamento de PII
                 *    grave entre atendimentos distintos.
                 *
                 * 2. `awaitingBot` e `welcomeInputLock`: se a conversa anterior terminou durante espera do bot
                 *    ou durante a trava inicial de boas-vindas, as flags bloqueavam o input na conversa B,
                 *    deixando o composer read-only ou com indicador de espera falso.
                 *
                 * 3. `agentTyping`: se o atendente humano da conversa A estava digitando quando o atendimento
                 *    foi encerrado, a conversa B (que começa com IA) nascia mostrando "Atendente digitando"
                 *    congelado para sempre.
                 */
                attachedFiles: undefined,
                awaitingBot: undefined,
                agentTyping: undefined,
                welcomeInputLock: undefined,
                /*
                 * 4. `conversationsClient`: o `handleStartNewChat` DESLIGA o client antes de
                 *    despachar este reset. Mantê-lo aqui deixaria o store apontando para um client
                 *    morto e afirmando que ele está vivo — e todo consumidor lê
                 *    `state.chat.conversationsClient` supondo um client utilizável. Sem client é um
                 *    estado honesto e já tratado: todos os leitores usam `?.`.
                 */
                conversationsClient: undefined
            };
        }
        case ACTION_UPDATE_CONVERSATION_STATE: {
            /*
             * 🔴 GUARDA DE PROCEDÊNCIA — impede encerrar visualmente um atendimento VIVO.
             *
             * Este estado chega por caminhos ASSÍNCRONOS (revalidação, evento do SDK). Se o
             * apostador iniciar uma conversa nova enquanto a leitura da anterior está em voo, a
             * resposta velha chega depois e, sem esta guarda, sobrescreve o estado da conversa
             * atual: `closed` da antiga derruba a nova, e o `MessagingCanvasPhase` troca o chat
             * pela tela de encerrado. O apostador é expulso de um atendimento em andamento e não
             * há erro nenhum na tela para explicar.
             *
             * Opt-in e retrocompatível: só descarta quando o payload DIZ de qual conversa veio e
             * essa conversa não é a corrente. Sem SID no payload, aplica como antes.
             */
            const { conversationSid } = action.payload;
            if (conversationSid && state.conversation?.sid && conversationSid !== state.conversation.sid) {
                return state;
            }
            return {
                ...state,
                conversationState: action.payload.conversationState
            };
        }
        case ACTION_UPDATE_CSAT_PENDING: {
            /*
             * Mesma guarda de procedência do estado da conversa, e pelo mesmo motivo: o sinal chega
             * por evento assíncrono do SDK, e um evento da conversa anterior pode aterrissar depois
             * de o apostador já estar em outra. Sem isto, a pesquisa de um atendimento encerrado
             * apareceria dentro de um atendimento novo.
             *
             * O estado guarda o booleano, não o SID — quem sabe qual é a conversa corrente é
             * `state.conversation`, e duplicar isso criaria uma segunda verdade.
             */
            const { conversationSid } = action.payload;
            if (conversationSid && state.conversation?.sid && conversationSid !== state.conversation.sid) {
                return state;
            }
            return {
                ...state,
                csatPending: action.payload.csatPending
            };
        }
        case ACTION_UPDATE_AWAITING_BOT: {
            return {
                ...state,
                awaitingBot: action.payload.awaitingBot
            };
        }
        case ACTION_UPDATE_WELCOME_INPUT_LOCK: {
            return {
                ...state,
                welcomeInputLock: action.payload.welcomeInputLock
            };
        }
        case ACTION_UPDATE_HUMAN_MODE: {
            return {
                ...state,
                humanMode: action.payload.humanMode
            };
        }
        case ACTION_UPDATE_AGENT_TYPING: {
            return {
                ...state,
                agentTyping: action.payload.agentTyping
            };
        }
        case ACTION_UPDATE_CSAT: {
            /*
             * MESMA guarda de procedência do `ACTION_UPDATE_CSAT_PENDING`, e pelo mesmo motivo: o POST
             * da nota é assíncrono e pode aterrissar depois de o apostador já estar em outra conversa.
             *
             * Sem ela, o merge parcial abaixo cria na conversa NOVA um pedaço da pesquisa da anterior.
             * O estrago não é cosmético: `useCsat` só busca quando `survey` está AUSENTE, então a
             * pesquisa legítima da conversa nova deixa de ser buscada e nunca aparece — sem erro, sem
             * log. E o comentário livre do apostador, que costuma carregar CPF, atravessa junto.
             *
             * Opt-in: quem não declara SID continua passando, para não quebrar os despachos internos
             * de status (`submitting`) que não têm como saber de qual conversa vieram.
             */
            const { conversationSid } = action.payload;
            if (conversationSid && state.conversation?.sid && conversationSid !== state.conversation.sid) {
                return state;
            }
            /*
             * Um DELTA não dá à luz o snapshot — só o seed do GET cria.
             *
             * `useCsat` busca a pesquisa apenas quando `chat.csat` está AUSENTE. Uma escrita parcial
             * que criasse o objeto tornaria `survey` verdadeiro sem nunca ter havido GET, e a
             * pesquisa daquela conversa não seria buscada NUNCA MAIS — sem erro e sem log. É a mesma
             * armadilha que a revisão do plano pegou na v1.
             *
             * O caso concreto: o cronômetro da faixa do atendimento está armado e, dentro da janela,
             * o `ACTION_START_SESSION` limpa o `csat`. Ele também roda no *resume*, onde o SID NÃO
             * muda — então a guarda de procedência acima não descarta, e o `faixaAposentada`
             * atrasado chegaria aqui com o estado já limpo.
             *
             * `requested` é o que separa os dois: só o snapshot completo do GET o carrega. Todo
             * delta (`submitting`, `submitted`, `readonly`, `idle`, `faixaAposentada`) nasce depois
             * do seed e não significa nada sozinho.
             */
            if (!state.csat && action.payload.csat?.requested !== true) {
                return state;
            }

            // Merge parcial: o seed do GET traz o objeto completo; as transições de status mandam só o delta.
            return {
                ...state,
                csat: { ...state.csat, ...action.payload.csat }
            };
        }

        default:
            return state;
    }
};
